import {Transition} from '@headlessui/react'
import classNames from 'classnames'
import React, {Fragment, PropsWithChildren, useEffect} from 'react'
import {createPortal} from 'react-dom'
import FocusLock from 'react-focus-lock'
import {useEventListener} from 'usehooks-ts'

import Backdrop from '../Backdrop'
import DrawerHeader from './DrawerHeader'

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  position: 'top' | 'right' | 'bottom' | 'left';
  disableBackdrop?: boolean;
}

const Drawer = ({open, onClose, position, children, disableBackdrop}: PropsWithChildren<DrawerProps>) => {

  // Disable main content scrolling when open
  useEffect(() => {
    open
      ? document.body.style.overflow = 'hidden'
      : document.body.style.overflow = 'unset'
  }, [open])

  // Close when pressing ESC
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && open) {
      onClose()
    }
  }

  const drawerClassNames = classNames('z-[110] fixed outline-none bg-kwota-neutral-100', {
    'right-0 top-0 w-full': position === 'top',
    'right-0 top-0 h-full': position === 'right',
    'right-0 bottom-0 w-full': position === 'bottom',
    'left-0 bottom-0 h-full': position === 'left'
  })

  const transitionTransformClassNames = classNames({
    '-translate-y-full': position === 'top',
    'translate-x-full': position === 'right',
    'translate-y-full': position === 'bottom',
    '-translate-x-full': position === 'left'
  })

  useEventListener('keydown', onKeyDown)

  const renderDrawer = () => {
    return (
      <>
        {open && !disableBackdrop && <Backdrop onClick={onClose} />}
        <FocusLock autoFocus={false}>
          <Transition
            show={open}
            as={Fragment}
            enter="transition-transform duration-300"
            enterFrom={transitionTransformClassNames}
            enterTo="translate-x-0 translate-y-0"
            leave="transition-transform duration-300"
            leaveFrom="translate-x-0 translate-y-0"
            leaveTo={transitionTransformClassNames}
          >
            <div
              aria-modal
              tabIndex={-1}
              role="dialog"
              className={drawerClassNames}
            >
              {children}
            </div>
          </Transition>
        </FocusLock>
      </>
    )
  }

  return createPortal(renderDrawer(), document.body)
}
export interface ModalHeaderProps {
  headerText: string;
  onClose?: () => void;
}

Drawer.Header = DrawerHeader

export default Drawer
